import InteractionDetailFormsController from "./interaction_detail_forms_controller";

// Modal-based interaction/reminder detail viewer.
// Used on the contacts index (interaction details with prev/next navigation),
// the contact page reminders panel, and the notifications page (single mode).
// Detail content is cloned from hidden [data-interaction-detail] templates.
export default class extends InteractionDetailFormsController {
  static targets = ["newFormModal", "detailModal", "detailContent", "modalType", "modalDate", "modalStatus"];

  connect() {
    // Single-item mode disables prev/next navigation (e.g. notifications page)
    const singleModeAttr = this.element.dataset.singleMode;
    this.singleMode = singleModeAttr === "true" || singleModeAttr === "1";
    this.handleEscape = this.handleEscape.bind(this);
    this.handleKeyNavigation = this.handleKeyNavigation.bind(this);

    this.interactionItems = [];
    this.currentInteractionIndex = -1;
  }

  openNewForm() {
    if (this.hasNewFormModalTarget) {
      this.newFormModalTarget.style.display = "flex";
      this.newFormModalTarget.classList.add("modal--visible");
      document.addEventListener("keydown", this.handleEscape);
      document.body.style.overflow = "hidden";
    }
  }

  closeNewForm() {
    this.closeModal();
  }

  closeModal() {
    if (this.hasNewFormModalTarget) {
      this.newFormModalTarget.classList.remove("modal--visible");
      this.newFormModalTarget.style.display = "none";
    }
    if (this.hasDetailModalTarget) {
      this.detailModalTarget.classList.remove("modal--visible");
      this.detailModalTarget.style.display = "none";
    }

    document.removeEventListener("keydown", this.handleEscape);
    document.body.style.overflow = "";
  }

  openDetail(event) {
    event.preventDefault();
    const interactionId = event.currentTarget.dataset.interactionId;

    // Contact ID scopes prev/next navigation to one contact's interactions
    let contactId = event.currentTarget.dataset.contactId;
    if (!contactId) {
      const controllerElement = event.currentTarget.closest('[data-controller*="interaction-modal"][data-contact-id]');
      contactId = controllerElement ? controllerElement.dataset.contactId : null;
    }
    if (!contactId) {
      const contactCard = event.currentTarget.closest(".contact-card[data-contact-id]");
      contactId = contactCard ? contactCard.dataset.contactId : null;
    }

    if (!this.singleMode && contactId) {
      // Clickable interaction items for this contact; reminders (trigger-*) don't paginate
      const selector = `[data-contact-id="${contactId}"] [data-interaction-id], [data-interaction-id][data-contact-id="${contactId}"]`;
      const seen = new Set();
      this.interactionItems = Array.from(document.querySelectorAll(selector))
        .filter((el) => el.hasAttribute("data-action") && el.dataset.action.includes("openDetail"))
        .filter((el) => !(el.dataset.interactionId || "").startsWith("trigger-"))
        .filter((el) => {
          const id = el.dataset.interactionId;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
    }

    this.currentInteractionIndex = this.singleMode
      ? -1
      : this.interactionItems.findIndex((item) => item.dataset.interactionId === interactionId);

    this.showInteractionDetail(interactionId);
  }

  showInteractionDetail(interactionId) {
    const detailElement = document.querySelector(`[data-interaction-detail="${interactionId}"]`);
    if (!detailElement) return;

    // Clone the content to avoid moving the original
    if (this.hasDetailContentTarget) {
      this.detailContentTarget.innerHTML = detailElement.innerHTML;
      this.addNavigationButtons();
    }

    // Populate modal header from the hidden meta spans in the template
    const typeEl = detailElement.querySelector("[data-interaction-type]");
    const dateEl = detailElement.querySelector("[data-interaction-date]");
    const statusEl = detailElement.querySelector("[data-interaction-status]");

    if (this.hasModalTypeTarget) {
      if (interactionId.startsWith("trigger-")) {
        this.modalTypeTarget.textContent = "Edit Reminder";
      } else if (typeEl) {
        this.modalTypeTarget.textContent = typeEl.textContent.trim();
      }
    }
    if (this.hasModalDateTarget && dateEl) {
      this.modalDateTarget.textContent = dateEl.textContent.trim();
    }
    if (this.hasModalStatusTarget && statusEl) {
      const status = statusEl.textContent.trim();
      if (status === "overdue") {
        this.modalStatusTarget.innerHTML = '<span class="contact-detail__modal-status-badge contact-detail__modal-status-badge--overdue">⚠️ OVERDUE</span>';
      } else if (status === "wrap_up") {
        this.modalStatusTarget.innerHTML = '<span class="contact-detail__modal-status-badge contact-detail__modal-status-badge--wrapup">📝 WRAP UP</span>';
      } else if (status === "upcoming") {
        this.modalStatusTarget.innerHTML = '<span class="contact-detail__modal-status-badge contact-detail__modal-status-badge--upcoming">📅 SCHEDULED</span>';
      } else {
        this.modalStatusTarget.innerHTML = "";
      }
    }

    if (this.hasDetailModalTarget) {
      this.detailModalTarget.style.display = "flex";
      this.detailModalTarget.classList.add("modal--visible");
    }
    document.addEventListener("keydown", this.handleEscape);
    document.addEventListener("keydown", this.handleKeyNavigation);
    document.body.style.overflow = "hidden";
  }

  addNavigationButtons() {
    if (this.singleMode) return;
    if (this.interactionItems.length <= 1) return;
    if (this.currentInteractionIndex == null || this.currentInteractionIndex < 0) return;
    const currentId = this.interactionItems[this.currentInteractionIndex]?.dataset.interactionId;
    if (currentId && currentId.startsWith("trigger-")) return;

    const navContainer = document.createElement("div");
    navContainer.className = "interaction-detail__navigation";
    navContainer.innerHTML = `
      <button class="interaction-detail__nav-button interaction-detail__nav-button--prev"
              data-action="click->interaction-modal#navigatePrev"
              ${this.currentInteractionIndex <= 0 ? "disabled" : ""}>
        ← Previous
      </button>
      <span class="interaction-detail__nav-counter">
        ${this.currentInteractionIndex + 1} of ${this.interactionItems.length}
      </span>
      <button class="interaction-detail__nav-button interaction-detail__nav-button--next"
              data-action="click->interaction-modal#navigateNext"
              ${this.currentInteractionIndex >= this.interactionItems.length - 1 ? "disabled" : ""}>
        Next →
      </button>
    `;

    this.detailContentTarget.insertBefore(navContainer, this.detailContentTarget.firstChild);
  }

  navigatePrev() {
    if (this.currentInteractionIndex > 0) {
      this.currentInteractionIndex--;
      this.showInteractionDetail(this.interactionItems[this.currentInteractionIndex].dataset.interactionId);
    }
  }

  navigateNext() {
    if (this.currentInteractionIndex < this.interactionItems.length - 1) {
      this.currentInteractionIndex++;
      this.showInteractionDetail(this.interactionItems[this.currentInteractionIndex].dataset.interactionId);
    }
  }

  // "Follow-up of:" reference — swap the modal to the parent's detail when its
  // template is on the page; otherwise the link navigates to the contact timeline.
  openParent(event) {
    const parentId = event.currentTarget.dataset.parentInteractionId;
    if (!document.querySelector(`[data-interaction-detail="${parentId}"]`)) return;
    event.preventDefault();

    this.currentInteractionIndex = this.interactionItems.findIndex(
      (item) => item.dataset.interactionId === parentId
    );
    this.showInteractionDetail(parentId);
  }

  closeDetail() {
    this.closeModal();
    document.removeEventListener("keydown", this.handleKeyNavigation);
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }

  handleKeyNavigation(event) {
    if (this.singleMode) return;
    const focused = document.activeElement;
    const isFormInput = ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName);

    if (!isFormInput && this.hasDetailModalTarget && this.detailModalTarget.style.display === "flex") {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.navigatePrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.navigateNext();
      }
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape);
    document.removeEventListener("keydown", this.handleKeyNavigation);
    document.body.style.overflow = "";
  }
}
